import { mapSlice } from '@atlaskit/editor-common/utils';
import type { Node as PMNode, Slice } from '@atlaskit/editor-prosemirror/model';
import type { Transaction } from '@atlaskit/editor-prosemirror/state';
import {
	getParameters,
	resolveRemixVersionId,
	setParameters,
} from '@atlaskit/native-embeds-common/utils';

import { buildDuplicatedMauiAppUrl, parseMauiAppLink } from './detect-maui-link';
import type { MauiAppLinkInfo } from './maui-diagram-paste-plugin';

export type PastedMauiApp = {
	linkInfo: MauiAppLinkInfo;
	localId: string;
	revisionId: string | undefined;
};

const getAppLink = (node: PMNode): MauiAppLinkInfo | null => {
	if (node.type.name !== 'extension') {
		return null;
	}
	const parameters = node.attrs.parameters;
	const url = getParameters(parameters, 'url') ?? parameters?.url;
	const link = typeof url === 'string' ? parseMauiAppLink(url) : null;
	if (!link) {
		return null;
	}
	return {
		...link,
		appId:
			resolveRemixVersionId({
				storedRemixAppId: getParameters(parameters, 'remixAppId'),
				url,
			}) ?? link.appId,
	};
};

const withAppUrl = (node: PMNode, url: string, remixAppId?: string) => ({
	...node.attrs,
	parameters: {
		...setParameters(node.attrs.parameters, { url, ...(remixAppId && { remixAppId }) }),
		...(typeof node.attrs.parameters?.url === 'string' && { url }),
	},
});

const getRevisionId = (node: PMNode): string | undefined => {
	const revisionId: unknown = node.attrs.parameters?.mauiEditRevisionId;
	return typeof revisionId === 'string' ? revisionId : undefined;
};

export const sliceContainsMauiApp = (slice: Slice): boolean => {
	let found = false;
	slice.content.descendants((node) => {
		found ||= getAppLink(node) !== null;
		return !found;
	});
	return found;
};

export const prepareMauiAppPaste = (slice: Slice): Slice =>
	mapSlice(slice, (node) => {
		const link = getAppLink(node);
		if (!link) {
			return node;
		}
		// The URL remains on the initial version during text editing. A new iframe must
		// load the edited version, and must not share the source embed's history identity.
		return node.type.create(
			{
				...withAppUrl(node, buildDuplicatedMauiAppUrl(link.originalUrl, link.appId)),
				localId: crypto.randomUUID(),
			},
			node.content,
			node.marks,
		);
	});

export const getPastedMauiApps = (slice?: Slice): PastedMauiApp[] => {
	const apps: PastedMauiApp[] = [];
	slice?.content.descendants((node) => {
		const linkInfo = getAppLink(node);
		if (linkInfo && typeof node.attrs.localId === 'string') {
			apps.push({
				linkInfo,
				localId: node.attrs.localId,
				revisionId: getRevisionId(node),
			});
		}
	});
	return apps;
};

export const updatePastedMauiApp = (
	tr: Transaction,
	target: PastedMauiApp,
	newAppId: string,
): void => {
	tr.doc.descendants((node, pos) => {
		if (node.attrs.localId !== target.localId) {
			return true;
		}
		const currentLink = getAppLink(node);
		// A user can edit, replace, or undo the paste while duplication is in flight.
		if (
			currentLink?.appId === target.linkInfo.appId &&
			currentLink.originalUrl === target.linkInfo.originalUrl &&
			getRevisionId(node) === target.revisionId
		) {
			tr.setNodeMarkup(
				pos,
				undefined,
				withAppUrl(node, buildDuplicatedMauiAppUrl(currentLink.originalUrl, newAppId), newAppId),
			);
			tr.setMeta('addToHistory', false);
		}
		return false;
	});
};

export const docContainsMauiAppVersion = (doc: PMNode, versionId: string): boolean => {
	let found = false;
	doc.descendants((node) => {
		if (getAppLink(node)?.appId === versionId) {
			found = true;
		}
		return !found;
	});
	return found;
};
